/* eslint-disable no-return-assign */
/* eslint-disable react/no-danger */
import cx from 'classnames';
import { lazy, Suspense, useEffect, useState, useRef } from 'react';
import { Grid, GridItem, Icon, Modal } from '@bwoty-web/ui-kit';
import {
  ICON_COMMS_LIKE,
  ICON_UI_SEARCH,
  ICON_UI_MENU,
  ICON_UI_CHEVRON_DOWN,
} from '@bwoty-web/ui-kit/icons';
import { useGlobals } from '@bwoty-web/ui-kit/hooks';
import Logotype from './components/logotype/Logotype';
import './header.scss';
import Dropdown from './components/dropdown/Dropdown';
import SkipLinks from './components/skipLinks/SkipLinks';
import LoggedInBanner from './components/loggedInPanel/myHotels/LoggedInBanner';

const Menu = lazy(() => import('./components/menu/Menu'));
const AlgoliaSearch = lazy(() =>
  import('./components/search/algolia/AlgoliaSearch'),
);

const Header = () => {
  const { content, hideNavigation, user, algoliaConfig, isMyHotels } =
    useGlobals();
  const {
    navigationLinks: mainLinks,
    skipLinksLabel,
    searchPlaceholderText,
  } = content || {};
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuLoaded, setMenuLoaded] = useState(false);
  const [activeMainItem, setActiveMainItem] = useState(null);
  const mainNavItemRefs = useRef([]);

  useEffect(() => {
    if (menuOpen && !menuLoaded) {
      setMenuLoaded(true);
    }
  }, [menuOpen, menuLoaded]);

  return (
    <>
      <header
        className={cx('header', {
          'header--no-navigation': hideNavigation,
        })}
      >
        <Grid root>
          <GridItem>
            <SkipLinks label={skipLinksLabel} />
            <nav className="header-nav">
              <Logotype />
              <ul
                className={cx('header-main-nav', {
                  'header-main-nav--open-sub-menu': activeMainItem,
                })}
              >
                {mainLinks?.map((link, mainLinkIndex) => {
                  const isActive = activeMainItem === link.id;
                  const hasSubLinks = link.subLinks?.length > 0;
                  const LinkType = hasSubLinks ? 'button' : 'a';
                  const linkProps = hasSubLinks
                    ? {
                        type: 'button',
                        onClick: () => setActiveMainItem(link.id),
                        'aria-expanded': isActive,
                        'data-cs-mask':
                          link.id === 'myPage' && !!user?.firstName,
                      }
                    : {
                        href: link?.mainLink?.href,
                      };

                  return (
                    <li
                      className={cx('header-main-nav__item', {
                        'header-main-nav__item--my-page': link.id === 'myPage',
                        'header-main-nav__item--active': isActive,
                      })}
                      key={link.alternativeText}
                      data-gtm-tag={link.gtm}
                    >
                      <LinkType
                        className={cx('header-main-nav__link', {
                          'header-main-nav__link--linked': !hasSubLinks,
                        })}
                        ref={(el) =>
                          (mainNavItemRefs.current[mainLinkIndex] = el)
                        }
                        {...linkProps}
                      >
                        {link.id === 'myPage' &&
                        user?.firstName &&
                        user?.firstName?.length < 15
                          ? user?.firstName
                          : link.alternativeText}

                        {hasSubLinks && (
                          <Icon path={ICON_UI_CHEVRON_DOWN} size="xxs" />
                        )}
                      </LinkType>
                      <Dropdown
                        links={link.subLinks}
                        onClose={() => setActiveMainItem(null)}
                        isActive={isActive}
                        mainLinkIndex={mainLinkIndex}
                        onClickOutsideException={
                          mainNavItemRefs.current[mainLinkIndex]
                        }
                      />
                    </li>
                  );
                })}
                {content?.showHeart && (
                  <li className="header-main-nav__item header-main-nav__item--icon">
                    <a href={content.heartLink?.href?.url}>
                      <span className="visually-hidden">
                        {content.heartLink?.title}
                      </span>
                      <Icon path={ICON_COMMS_LIKE} size="s" />
                    </a>
                  </li>
                )}
                {searchPlaceholderText && (
                  <li className="header-main-nav__item header-main-nav__item--icon">
                    <button
                      onClick={() => setSearchOpen(!searchOpen)}
                      type="button"
                      aria-haspopup="dialog"
                    >
                      <span className="visually-hidden">
                        {searchPlaceholderText}
                      </span>
                      <Icon path={ICON_UI_SEARCH} size="s" />
                    </button>
                  </li>
                )}
                {mainLinks?.length > 0 && (
                  <li className="header-main-nav__item header-main-nav__item--icon header-main-nav__item--menu">
                    <button
                      type="button"
                      aria-expanded={menuOpen}
                      aria-haspopup="dialog"
                      onClick={() => setMenuOpen(true)}
                    >
                      <Icon path={ICON_UI_MENU} size="s" />
                      <span className="visually-hidden">
                        {content?.menuLabel}
                      </span>
                    </button>
                  </li>
                )}
              </ul>
            </nav>
          </GridItem>
        </Grid>
      </header>
      {isMyHotels && user?.isLoggedIn && <LoggedInBanner />}
      {searchOpen && (
        <Suspense fallback={<div />}>
          <Modal size="l" onClose={() => setSearchOpen(false)}>
            <AlgoliaSearch config={algoliaConfig} />
          </Modal>
        </Suspense>
      )}
      {menuLoaded && (
        <Suspense fallback={<div />}>
          <Menu
            isOpen={menuOpen}
            openSearch={() => setSearchOpen(true)}
            onClose={() => setMenuOpen(false)}
          />
        </Suspense>
      )}
    </>
  );
};

export default Header;
